import React from 'react';
import { Save, Play } from 'lucide-react';

const TENANT_MODES = [
  { id: 'select', label: 'Select' },
  { id: 'all', label: 'All' },
];

const QueryEditor = ({
  queryText,
  setQueryText,
  tenantMode = 'select',
  setTenantMode,
  selectedTenants = [],
  setSelectedTenants,
  tenants = [],
  onExecute,
  onSave,
  executing,
}) => {
  const canExecute = () => {
    if (!queryText.trim() || executing) return false;
    if (tenantMode === 'select') return selectedTenants.length > 0;
    return true; // 'all'
  };

  const toggleTenant = (t) => {
    setSelectedTenants((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const tenantCount = tenantMode === 'all' ? tenants.length : selectedTenants.length;

  return (
    <>
      <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-col gap-3 bg-gray-50 dark:bg-slate-950">
        {/* Top row: mode toggle + count + actions */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden">
              {TENANT_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setTenantMode(mode.id)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    tenantMode === mode.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                  } ${mode.id !== 'select' ? 'border-l border-gray-300 dark:border-slate-600' : ''}`}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <span className="text-xs text-gray-500 dark:text-slate-400">
              {tenantMode === 'all'
                ? `All ${tenants.length} tenants`
                : `${selectedTenants.length} of ${tenants.length} selected`}
            </span>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={!queryText.trim()}
              className="flex items-center px-4 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              Save
            </button>
            <button
              onClick={onExecute}
              disabled={!canExecute()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Play size={16} className="mr-2" />
              {executing ? 'Running...' : `Run on ${tenantCount}`}
            </button>
          </div>
        </div>

        {/* Tenant checkboxes for 'select' mode */}
        {tenantMode === 'select' && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedTenants(selectedTenants.length === tenants.length ? [] : [...tenants])}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-1"
            >
              {selectedTenants.length === tenants.length ? 'Clear all' : 'Select all'}
            </button>
            {tenants.map((t) => (
              <label key={t} className="inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTenants.includes(t)}
                  onChange={() => toggleTenant(t)}
                  className="rounded w-3.5 h-3.5"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">{t}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-[200px] bg-[#1e1e1e] p-6 font-mono text-sm overflow-auto">
        <textarea
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          placeholder="-- Enter your SQL query here&#10;SELECT TOP 10 * FROM Event WHERE Active = 1"
          spellCheck={false}
          className="w-full h-full min-h-[180px] bg-transparent text-gray-300 placeholder-gray-500 resize-none focus:outline-none focus:ring-0"
          style={{ caretColor: '#fff' }}
        />
      </div>
    </>
  );
};

export default QueryEditor;
